import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/services/auth.service';
import { DrupalRESTService } from 'src/app/services/drupal-rest.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { LoggerService } from 'src/app/services/logger.service';

/**
 * Online Booking settings (configuration bundle `online_booking`, reached
 * through the studio's field_online_booking_ref): switches the public
 * website booking widget on and sets the scheduling
 * window. Every web booking lands on the Website Booking service account
 * (inquiry taker, executive and teacher) until staff reassign it.
 */
@Component({
  selector: 'app-online-booking',
  templateUrl: './online-booking.component.html',
  styleUrls: ['./online-booking.component.css']
})
export class OnlineBookingComponent implements OnInit {

  readonly configFieldName = 'field_online_booking_ref';

  form: FormGroup;
  showProgressSpinner = false;
  successMessage = '';
  errorMessage = '';
  copied: '' | 'embed' = '';
  /** "<host>/<slug>/" when the studio has a website slug: the only place the
   *  form will load. '' = no slug, embeddable anywhere on the studio sites. */
  website = '';
  websiteChecked = false;
  /** The studio's random embed key (studio.field_booking_key, generated at creation). */
  embedKey = '';

  constructor(
    private fb: FormBuilder,
    private drupalREST: DrupalRESTService,
    private authService: AuthService,
    private permissions: PermissionsService,
    private router: Router,
  ) {
    this.form = this.fb.group({
      field_booking_enabled: [false],
      field_booking_days_ahead: [14, [Validators.required, Validators.min(1), Validators.max(90)]],
      field_booking_lead_hours: [2, [Validators.required, Validators.min(0), Validators.max(168)]],
      field_booking_slot_minutes: [30, [Validators.required, Validators.min(5), Validators.max(120)]],
    });
  }

  ngOnInit(): void {
    // Super-admins only while online booking is being rolled out; the menu
    // link is hidden for everyone else, this covers a typed URL.
    if (!this.permissions.isSuperAdmin()) {
      this.router.navigate(['/configuration']);
      return;
    }
    this.load();
    this.loadEmbedKey();
  }

  /** The key lives on the studio, not the booking settings. */
  loadEmbedKey(): void {
    this.drupalREST.httpGET('/api/v1/booking-embed-key').subscribe({
      next: (response: any) => {
        this.embedKey = response?.key || '';
        this.website = response?.website || '';
        this.websiteChecked = true;
        this.embedPackages = Array.isArray(response?.packages) ? response.packages : [];
        if (!this.embedPackages.some(p => p.slug === this.embedPackage)) {
          this.embedPackage = this.embedPackages[0]?.slug || 'de';
        }
      },
      error: (error) => LoggerService.error('Online booking embed key load failed', error),
    });
  }

  get studioId(): string {
    return String(this.authService.selectedStudio?.id ?? '');
  }

  /**
   * The widget and its loader are served by this Angular app, so the URLs
   * use the page's own origin. environment.hostUrl is the Drupal API host
   * (app.am.arthurmurray.com), which serves neither.
   */
  get appOrigin(): string {
    return window.location.origin;
  }

  /**
   * 'form' = the booking card only, for a page that has its own headline
   * and copy (the default). 'full' = the card plus the headline column.
   */
  embedView: 'form' | 'full' = 'form';
  /**
   * Which of the studio's intro system packages the snippet books
   * (data-ami-booking-package). The list comes from the embed-key call:
   * each studio owns its own Dance Evaluation / Pre-Original /
   * Pre-Original (Couple) packages, shown by their current names.
   */
  embedPackage = 'de';
  embedPackages: { slug: string; name: string; minutes: number }[] = [];

  get widgetUrl(): string {
    const params = [];
    if (this.embedView === 'form') { params.push('view=form'); }
    params.push(`package=${this.embedPackage}`);
    return `${this.appOrigin}/widgets/booking/${this.embedKey || '<embed key>'}${params.length ? '?' + params.join('&') : ''}`;
  }

  get loaderUrl(): string {
    return `${this.appOrigin}/assets/booking.js`;
  }

  /**
   * The loader script mounts the form-only widget into the marked element
   * and keeps the frame as tall as the form (src/assets/booking.js).
   */
  get embedSnippet(): string {
    const view = this.embedView === 'full' ? ' data-ami-booking-view="full"' : '';
    const pkg = ` data-ami-booking-package="${this.embedPackage}"`;
    return `<div data-ami-booking="${this.embedKey || '<embed key>'}"${view}${pkg}></div>\n<script src="${this.loaderUrl}" async></script>`;
  }


  load(): void {
    this.showProgressSpinner = true;
    this.drupalREST.httpGET(`/api_rest/v1/loadStudioConfig?config_field_name=${this.configFieldName}`).subscribe({
      next: (response: any) => {
        this.showProgressSpinner = false;
        if (!response || response.error) {
          return; // Nothing saved yet: keep the defaults.
        }
        this.form.patchValue({
          field_booking_enabled: response.field_booking_enabled?.[0]?.value === '1' || response.field_booking_enabled?.[0]?.value === true,
          field_booking_days_ahead: Number(response.field_booking_days_ahead?.[0]?.value ?? 14),
          field_booking_lead_hours: Number(response.field_booking_lead_hours?.[0]?.value ?? 2),
          field_booking_slot_minutes: Number(response.field_booking_slot_minutes?.[0]?.value ?? 30),
        });
      },
      error: (error) => {
        this.showProgressSpinner = false;
        this.errorMessage = 'Could not load the online booking settings.';
        LoggerService.error('Online booking settings load failed', error);
      }
    });
  }

  save(): void {
    this.successMessage = '';
    this.errorMessage = '';
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.errorMessage = 'Please enter a valid scheduling window.';
      return;
    }
    const v = this.form.value;
    const postData = {
      config_field_name: this.configFieldName,
      config_data: {
        field_booking_enabled: v.field_booking_enabled ? 1 : 0,
        field_booking_days_ahead: v.field_booking_days_ahead,
        field_booking_lead_hours: v.field_booking_lead_hours,
        field_booking_slot_minutes: v.field_booking_slot_minutes,
      }
    };
    this.showProgressSpinner = true;
    this.drupalREST.httpPOST('/api_rest/v1/saveStudioConfig', postData).subscribe({
      next: () => {
        this.showProgressSpinner = false;
        this.successMessage = 'Online booking settings saved.';
      },
      error: (error) => {
        this.showProgressSpinner = false;
        this.errorMessage = 'Could not save the online booking settings.';
        LoggerService.error('Online booking settings save failed', error);
      }
    });
  }

  copy(text: string, which: 'embed'): void {
    navigator.clipboard?.writeText(text).then(() => {
      this.copied = which;
      setTimeout(() => this.copied = '', 2000);
    });
  }
}
